import { push } from 'redux-first-history';
import { call, put, select, takeEvery, takeLatest, delay, all } from 'redux-saga/effects';
import { userFound, userExpired, USER_SIGNED_OUT } from 'redux-oidc';
import { userContext } from '../userManager';
import {
  USER_GET,
  USER_PATCH,
  USER_ADDRESSES_GET,
  USER_ADDRESSES_PATCH,
  USER_ADDRESSES_DELETE,
  USER_DOCUMENTS_GET,
  ERROR_PAGE_SET_ERROR,
  USER_LOAD,
  USER_UPDATE,
  USER_NOTIFICATIONS_GET,
  USER_NOTIFICATIONS_READ,
  USER_ADDRESSES_POST,
  USER_DOCUMENTS_POST,
  USER_DOCUMENTS_PATCH,
  USER_PROFILE_IMAGE_GET,
  USER_PROFILE_IMAGE_POST,
  USER_PROFILE_IMAGE_DELETE,
  USERS_POST,
  USERS_GET,
  USER_DETAILS_PATCH,
  USER_MAIN_CONTACTS_PATCH,
  USER_MAIN_CONTACTS_PATCH_OTP,
  USER_MEETINGS_GET,
  USER_MEETING_TYPES_GET,
} from '../actions/types';
import { actionSpinnerClose, actionSpinnerOpen, actionModalOpen } from '../actions';
import { selectUser, selectUserId } from '../selectors';
import {
  apiUserNotifications,
  apiUserNotificationsRead,
  apiUserDetailsGet,
  apiUserDocumentsGet,
  apiUserAddressesGet,
  apiUserPatch,
  apiUserGet,
  apiUserAddressPost,
  apiUserDocumentPost,
  apiUsersGet,
  apiUserPost,
  apiUserProfileImageGet,
  apiUserProfileImagePost,
  apiUserProfileImageDelete,
  apiUserDetailsPatch,
  apiUserMainContactsPatch,
  apiUserMeetingsGet,
  apiUserMeetingTypesGet,
} from '../api/user';
import { getServerErrorMessage } from '../../utils';
import { createQadraSaga, handleOtp } from './shared';
import { apiAddressDelete, apiAddressPatch } from '../api/addresses';
import { documentFilePost } from './documents';
import { apiDocumentPatch, apiDocumentFileDelete } from '../api/documents';
import { otpIds } from '../../utils/otp';

/* user */

function* loadUser() {
  let errorMessage;
  let userData = null;
  try {
    yield put(actionSpinnerOpen());
    const user = yield call(() => userContext.userManager.getUser());
    if (user && !user.expired) {
      yield put(userFound(user));
      userData = yield call(apiUserGet, user.profile.userId);
    } else {
      const user2 = yield call(() => userContext.userManager.signinSilent());
      if (user2 && !user2.expired) {
        yield put(userFound(user2));
        userData = yield call(apiUserGet, user2.profile.userId);
      } else {
        yield put(userExpired());
      }
    }
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
    console.error('load user failed : ', e.message);
    errorMessage = e.message;
    yield put({ type: ERROR_PAGE_SET_ERROR, payload: { error: e.message } });
  } finally {
    if (userData) {
      const { user } = userData;
      const userDetails = yield call(apiUserDetailsGet, {
        id: user.id,
        userType: user.userType,
      });
      user.details = userDetails ? userDetails.detail : {};

      yield put({ type: USER_LOAD._SUCCESS, payload: { user } });
    } else {
      yield put({ type: USER_LOAD._ERROR, payload: { user: null } });
    }
    if (!userData && errorMessage !== 'login_required') yield put(push('/404'));
    yield put(actionSpinnerClose());
  }
}

export function* userUpdate() {
  const userId = yield select((s) => s.app.user.id);
  const { user } = yield call(apiUserGet, userId);
  yield put({ type: USER_UPDATE, payload: { user } });
}

/* auto-sign-out when user delete manually cookies! */
function* userLoggedOut() {
  try {
    localStorage.clear();
    sessionStorage.clear();
    yield put(push('/'));
  } catch (e) {
    // eslint-disable-next-line no-console
    console.log(e);
  }
}

function* watchUserLoggedOut() {
  yield takeEvery(USER_SIGNED_OUT, userLoggedOut);
}

function* usersGet() {
  const { users } = yield call(apiUsersGet);
  yield put({ type: USERS_GET._SUCCESS, payload: { users } });
}

const watchUsersGet = createQadraSaga(usersGet, {
  action: USERS_GET._REQUEST,
});

function* userGet({ payload: { userId } }) {
  const loggedUserId = yield select((s) => s.app.user.id);

  const { user } = yield call(apiUserGet, userId);
  const userDetails = yield call(apiUserDetailsGet, {
    id: userId,
    userType: user.userType,
  });
  user.details = userDetails ? userDetails.detail : {};

  if (loggedUserId === userId) {
    yield put({ type: USER_UPDATE, payload: { user } });
  }

  yield put({ type: USER_GET._SUCCESS, payload: { user } });
}

const watchUserGet = createQadraSaga(userGet, {
  action: USER_GET._REQUEST,
});

function* usersPost({ payload: { backUrl, toOpenModalId, ...user } }) {
  yield call(apiUserPost, user);
  yield usersGet();

  if (backUrl) yield put(push(backUrl));
  if (toOpenModalId) yield put(actionModalOpen(toOpenModalId));
}

const watchUsersPost = createQadraSaga(usersPost, {
  action: USERS_POST._REQUEST,
});

export function* userPatch({ payload: { id, backUrl, ...user } }) {
  yield call(apiUserPatch, id, user);
  yield userGet({ payload: { userId: id } });

  if (backUrl) yield put(push(backUrl));
}

const watchUsersPatch = createQadraSaga(userPatch, {
  action: USER_PATCH._REQUEST,
});

function* userDetailsPatch({ payload: { id, userType, backUrl, ...details } }) {
  yield call(apiUserDetailsPatch, { id, userType, details });
  yield userGet({ payload: { userId: id } });

  if (backUrl) yield put(push(backUrl));
}

const watchUserDetailsPatch = createQadraSaga(userDetailsPatch, {
  action: USER_DETAILS_PATCH._REQUEST,
});

function* userMainContactsPatch({ payload: { id, email, phonePrefix, phoneNumber, otp, url } }) {
  yield call(apiUserMainContactsPatch, { id, email, phonePrefix, phoneNumber, otp });

  // if the user is changing his email, logout
  const user = yield select((s) => selectUser(s, selectUserId(s)));
  if (user.id === id && user.email !== email) {
    yield call(() => userContext.userManager.signoutRedirect());
    sessionStorage.clear();
    localStorage.clear();
  } else {
    yield userGet({ payload: { userId: id } });
    if (url) yield put(push(url));
  }
}

const watchUserMainContactsPatch = createQadraSaga(userMainContactsPatch, {
  action: USER_MAIN_CONTACTS_PATCH._REQUEST,
  ...handleOtp(otpIds.updateUserMainContacts),
});

function* userMainContactsPatchOtp({ payload: { id, otp, email, phonePrefix, phoneNumber } }) {
  yield call(apiUserMainContactsPatch, { id, otp, email, phonePrefix, phoneNumber });
}

const watchUserMainContactsPatchOtp = createQadraSaga(userMainContactsPatchOtp, {
  action: USER_MAIN_CONTACTS_PATCH_OTP._REQUEST,
  hideSpinner: true,
  ...handleOtp(otpIds.updateUserMainContacts),
});

/* notifications */

function* userNotificationsGet({ payload: { userId } }) {
  const isDev = process.env.NODE_ENV === 'development';
  const pollingTime = isDev ? 60000 * 3 : 60000;

  while (true) {
    try {
      const { notifications } = yield call(apiUserNotifications, userId);
      yield put({
        type: USER_NOTIFICATIONS_GET._SUCCESS,
        payload: { notifications },
      });
      yield delay(pollingTime);
    } catch (error) {
      if (isDev) {
        // eslint-disable-next-line no-console
        console.warn(getServerErrorMessage(error));
      }
      yield delay(pollingTime);
    }
  }
}

function* watchUserNotificationsGet() {
  yield takeLatest(USER_NOTIFICATIONS_GET._REQUEST, userNotificationsGet);
}

function* userNotificationsRead({ payload: { userId } }) {
  try {
    yield call(apiUserNotificationsRead, userId);
    // update notifications without waiting for the polling
    const { notifications } = yield call(apiUserNotifications, userId);
    yield put({
      type: USER_NOTIFICATIONS_GET._SUCCESS,
      payload: { notifications },
    });
  } catch (error) {
    if (process.env.NODE_ENV !== 'production') {
      // eslint-disable-next-line no-console
      console.warn(getServerErrorMessage(error));
    }
  }
}

function* watchUserNotificationsRead() {
  yield takeLatest(USER_NOTIFICATIONS_READ._REQUEST, userNotificationsRead);
}

/* addresses */

function* userAddressesGet({ payload: { userId } }) {
  const { addresses } = yield call(apiUserAddressesGet, userId);
  yield put({ type: USER_ADDRESSES_GET._SUCCESS, payload: { addresses, userId } });
}

const watchUserAddressesGet = createQadraSaga(userAddressesGet, {
  action: USER_ADDRESSES_GET._REQUEST,
});

function* userAddressDelete({ payload: { addressId, userId } }) {
  yield call(apiAddressDelete, addressId);
  yield userAddressesGet({ payload: { userId } });
}

const watchUserAddressDelete = createQadraSaga(userAddressDelete, {
  action: USER_ADDRESSES_DELETE._REQUEST,
});

function* userAddressPost({ payload: { userId, backUrl, ...address } }) {
  yield call(apiUserAddressPost, userId, address);
  yield userAddressesGet({ payload: { userId } });
  if (backUrl) yield put(push(backUrl));
}

const watchUserAddressPost = createQadraSaga(userAddressPost, {
  action: USER_ADDRESSES_POST._REQUEST,
});

function* userAddressPatch({ payload: { userId, backUrl, ...address } }) {
  yield call(apiAddressPatch, address.id, address);
  yield userAddressesGet({ payload: { userId } });
  if (backUrl) yield put(push(backUrl));
}

const watchUserAddressPatch = createQadraSaga(userAddressPatch, {
  action: USER_ADDRESSES_PATCH._REQUEST,
});

/* documents */

function* userDocumentsGet({ payload: { userId } }) {
  const { documents } = yield call(apiUserDocumentsGet, userId);
  const sortedDocuments = [...documents].sort((a, b) =>
    new Date(a.createdAt) > new Date(b.createdAt) ? -1 : 1
  );
  yield put({ type: USER_DOCUMENTS_GET._SUCCESS, payload: { documents: sortedDocuments, userId } });
}

const watchUserDocumentsGet = createQadraSaga(userDocumentsGet, {
  action: USER_DOCUMENTS_GET._REQUEST,
});

function* userDocumentsPost({ payload: { userId, filesToUpload, backUrl, ...documentParams } }) {
  const { document } = yield call(apiUserDocumentPost, userId, documentParams);

  if (filesToUpload?.length > 0) {
    const calls = filesToUpload.map((f) =>
      documentFilePost({ file: f, documentId: document.id, showSpinner: false })
    );
    yield all(calls);
  }

  yield userDocumentsGet({ payload: { userId } });

  if (backUrl) yield put(push(backUrl));
}

const watchUserDocumentsPost = createQadraSaga(userDocumentsPost, {
  action: USER_DOCUMENTS_POST._REQUEST,
});

function* userDocumentsPatch({
  payload: { userId, documentId, filesToUpload, filesToDelete, backUrl, ...documentParams },
}) {
  yield call(apiDocumentPatch, documentId, documentParams);

  if (filesToUpload?.length > 0) {
    const calls = filesToUpload.map((f) =>
      documentFilePost({ file: f, documentId, showSpinner: false })
    );
    yield all(calls);
  }

  if (filesToDelete?.length > 0) {
    const calls = filesToDelete.map((f) => call(apiDocumentFileDelete, documentId, f.id));
    yield all(calls);
  }

  yield userDocumentsGet({ payload: { userId } });

  if (backUrl) yield put(push(backUrl));
}

const watchUserDocumentsPatch = createQadraSaga(userDocumentsPatch, {
  action: USER_DOCUMENTS_PATCH._REQUEST,
});

/* profile image */

function* userProfileImageGet({ payload: { userId } }) {
  const res = yield call(apiUserProfileImageGet, userId);
  let profileImage;
  if (res?.profileImage?.imageBase64) {
    profileImage = {
      data: res.profileImage.imageBase64,
      extension: res.profileImage.extension,
    };
  }
  yield put({ type: USER_PROFILE_IMAGE_GET._SUCCESS, payload: { profileImage, userId } });
}

const watchUserProfileImageGet = createQadraSaga(userProfileImageGet, {
  action: USER_PROFILE_IMAGE_GET._REQUEST,
  hideErrorByStatus: [404],
});

function* userProfileImagePost({ payload: { userId, profileImage } }) {
  yield call(apiUserProfileImagePost, userId, { profileImage });
  yield userProfileImageGet({ payload: { userId } });
}

const watchUserProfileImagePost = createQadraSaga(userProfileImagePost, {
  action: USER_PROFILE_IMAGE_POST._REQUEST,
  hideErrorByStatus: [404],
});

function* userProfileImageDelete({ payload: { userId } }) {
  yield call(apiUserProfileImageDelete, userId);
  yield put({
    type: USER_PROFILE_IMAGE_GET._SUCCESS,
    payload: { profileImage: undefined, userId },
  });

  // we could call the get and use the onError callback
  // but doing this way we don't send an useless request
  // yield userProfileImageGet({ payload: { userId } });
}

const watchUserProfileImageDelete = createQadraSaga(userProfileImageDelete, {
  action: USER_PROFILE_IMAGE_DELETE._REQUEST,
});

export function* userMeetingsGet({ payload: { userId, start, end, email } }) {
  const { collection } = yield call(apiUserMeetingsGet, userId, start, end, email);
  yield put({
    type: USER_MEETINGS_GET._SUCCESS,
    payload: { userMeetings: collection, userId },
  });
}

const watchUserMeetingsGet = createQadraSaga(userMeetingsGet, {
  action: USER_MEETINGS_GET._REQUEST,
});

export function* userMeetingTypesGet({ payload: { userId } }) {
  const { collection } = yield call(apiUserMeetingTypesGet, userId);
  yield put({
    type: USER_MEETING_TYPES_GET._SUCCESS,
    payload: { meetingTypes: collection, userId },
  });
}

const watchUserMeetingTypesGet = createQadraSaga(userMeetingTypesGet, {
  action: USER_MEETING_TYPES_GET._REQUEST,
});

export default [
  loadUser,
  watchUserGet,
  watchUsersGet,
  watchUsersPost,
  watchUsersPatch,
  watchUserMainContactsPatch,
  watchUserMainContactsPatchOtp,
  watchUserDetailsPatch,
  watchUserLoggedOut,
  watchUserNotificationsGet,
  watchUserNotificationsRead,
  watchUserAddressesGet,
  watchUserAddressDelete,
  watchUserAddressPatch,
  watchUserAddressPost,
  watchUserDocumentsGet,
  watchUserDocumentsPost,
  watchUserDocumentsPatch,
  watchUserProfileImageGet,
  watchUserProfileImagePost,
  watchUserProfileImageDelete,
  watchUserMeetingsGet,
  watchUserMeetingTypesGet,
];
